import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { PantallaCargando } from '@/components/Cargando';
import { Button } from '@/components/ui/button';
import {
  Send, FileText, BarChart3, CheckCircle2, Link2, AlertTriangle, MessageCircle,
} from 'lucide-react';

export default function WhatsAppHome() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tenant/marketing/templates').catch(() => ({ data: [] })),
      api.get('/tenant/whatsapp/business/status').catch(() => ({ data: null })),
    ]).then(([t, b]) => {
      const tpls = t.data || [];
      const approved = tpls.filter(x =>
        x.external_status === 'APPROVED' || x.status === 'active'
      ).length;
      setStats({ plantillas: tpls.length, aprobadas: approved });
      setBiz(b.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  const connected = !!biz?.connected;

  return (
    <div className="space-y-6" data-testid="whatsapp-home">
      <div>
        <h2 className="text-base font-semibold text-[#0F172A]">¿Qué quieres hacer?</h2>
        <p className="text-sm text-[#64748B] mt-1">
          Campañas y plantillas con la API oficial de WhatsApp Business (Meta).
        </p>
      </div>

      {/* Connection banner */}
      <button
        type="button"
        onClick={() => navigate('/tenant/whatsapp/conexion')}
        className={`w-full text-left rounded-2xl border p-4 flex items-start gap-3 transition-colors ${
          connected
            ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300'
            : 'border-amber-300 bg-amber-50 hover:border-amber-400'
        }`}
        data-testid="wa-home-conexion"
      >
        {connected ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
        ) : (
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#0F172A]">
            {connected ? 'API de WhatsApp Business conectada' : 'Conecta la API de WhatsApp Business'}
          </p>
          <p className="text-xs text-[#64748B] mt-0.5">
            {connected
              ? `${biz.active?.display_name || 'Meta'} · listo para plantillas y envíos`
              : 'Un clic con Meta o pega Phone Number ID + token. Sin esto no salen campañas.'}
          </p>
        </div>
        <span className="text-[11px] font-semibold text-[#128C7E] shrink-0">
          {connected ? 'Ver conexión →' : 'Conectar →'}
        </span>
      </button>

      <div className="grid sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => navigate('/tenant/whatsapp/envios')}
          className="text-left rounded-2xl border border-[#128C7E]/30 bg-[#F0FDF9] p-4 hover:border-[#128C7E]"
          data-testid="wa-home-envios">
          <Send className="w-5 h-5 text-[#128C7E] mb-2" />
          <p className="text-sm font-bold text-[#0F172A]">Envíos masivos</p>
          <p className="text-[11px] text-[#64748B]">Historial y nuevos disparos</p>
        </button>
        <button type="button" onClick={() => navigate('/tenant/whatsapp/plantillas')}
          className="text-left rounded-2xl border border-[#E2E8F0] bg-white p-4 hover:border-[#CBD5E1]"
          data-testid="wa-home-plantillas">
          <FileText className="w-5 h-5 text-[#64748B] mb-2" />
          <p className="text-sm font-bold text-[#0F172A]">Plantillas Meta</p>
          <p className="text-[11px] text-[#64748B]">Crear, enviar a revisión y usar</p>
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4" /> Resumen
          </h3>
          <button type="button" className="text-[11px] text-[#128C7E] underline"
            onClick={() => navigate('/tenant/whatsapp/informes')}>
            Informes →
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl border bg-white p-3">
            <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
              <Link2 className="w-3 h-3" /> API
            </p>
            <p className={`text-lg font-bold ${connected ? 'text-emerald-600' : 'text-amber-600'}`}>
              {connected ? 'OK' : 'Pendiente'}
            </p>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
              <FileText className="w-3 h-3" /> Plantillas
            </p>
            <p className="text-2xl font-bold text-[#0F172A]">{stats?.plantillas ?? 0}</p>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Aprobadas
            </p>
            <p className="text-2xl font-bold text-emerald-600">{stats?.aprobadas ?? 0}</p>
          </div>
        </div>
      </div>

      {!connected && (
        <div className="rounded-xl border border-[#128C7E]/20 bg-[#F0FDF9] p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#128C7E]" />
            <p className="text-sm text-[#0F172A] font-medium">Empieza por la conexión API</p>
          </div>
          <Button className="bg-[#128C7E] hover:bg-[#0F766E] text-white"
            onClick={() => navigate('/tenant/whatsapp/conexion')}
            data-testid="wa-home-cta-connect">
            Conectar WhatsApp Business
          </Button>
        </div>
      )}
    </div>
  );
}
